import PropTypes from 'prop-types';
import { useEffect } from 'react';

import {
  useColorModeValue,
  Text,
  Box,
  IconButton,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Tooltip,
  LinkBox,
} from '@chakra-ui/react';

import { Link as ReactRouterLink } from 'react-router-dom';

import { DragHandleIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { HiDotsVertical } from 'react-icons/hi';
import { RiAiGenerate } from 'react-icons/ri';
import { subquestActions } from 'store/modules/subquest/subquest-slice';
import { companionActions } from 'companion/store/companion-slice';
import {
  getRelatedCourseQuestBySubQuest,
  getSubQuestById,
} from 'store/modules/subquest/subquest-action';
import { useSelector, useDispatch } from 'react-redux';

import { ReducerStatus, CompanionMode } from 'utils/constants';
import { removeHtmlTags } from 'utils/textParser';
import { BiPieChart } from 'react-icons/bi';
import EmailGroup from 'components/common/fields/EmailGroup';
import IdTag from 'components/common/fields/IdTag';
import SubQuestIcon from 'components/subQuest/SubQuestIcon';
import PublishButton from 'components/common/buttons/PublishButton';
import { GrDetach } from 'react-icons/gr';

function SubQuestAdminItem({
  id,
  courseId,
  questId,
  questName,
  isEditable,
  isPreviewMode,
  subQuest,
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const dispatch = useDispatch();

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  const status = useSelector((state) => state.subquest.getSubQuest.status);
  const getSubQuest = useSelector((state) => state.subquest.getSubQuest);
  const selectedSubQuest = useSelector((state) => state.subquest.subQuest);
  const quest = useSelector((state) => state.quest);
  const course = useSelector((state) => state.course.course);

  useEffect(() => {
    if (getSubQuest.status === ReducerStatus.SUCCEEDED && selectedSubQuest) {
      const typeId = selectedSubQuest.type.id;
      if (typeId === 1) dispatch(subquestActions.toggleVideoUpdateModal(true));
      if (typeId === 2)
        dispatch(subquestActions.toggleNarrativeUpdateModal(true));
      if (typeId === 5)
        dispatch(
          subquestActions.setUpdateFileSubmission({
            modal: true,
            id: selectedSubQuest.id,
          })
        );
      if (typeId === 10)
        dispatch(subquestActions.toggleInteractiveContentUpdateModal(true));
    }
  }, [getSubQuest, selectedSubQuest, dispatch]);

  const editSubQuest = (subQuestId) => {
    dispatch(subquestActions.setQuestId(questId));
    dispatch(getSubQuestById({ questId, subQuestId }))
      // Checks the resolved sub-quest type; if it's a quiz (typeId === 4) opens the Update Quiz page in a new tab with courseId and questId query params.
      // Ensures navigation/decision happens only after the getSubQuestById thunk completes so the resolved id and type are accurate.
      .unwrap()
      .then((result) => {
        const resolvedId = result?.id ?? subQuestId;
        const resolvedTypeId = result?.type?.id;
        if (resolvedTypeId === 4) {
          window.open(
            `/admin/quizzes/edit/${resolvedId}?courseId=${courseId}&questId=${questId}`,
            '_blank'
          );
        }
      });
  };

  const removeSubQuest = (subQuestId) => {
    dispatch(subquestActions.setQuestId(questId));
    dispatch(subquestActions.setSubQuestIdForRemoval(subQuestId));
    dispatch(
      subquestActions.setRelatedCourseQuests({
        courseName: course.name,
        questName,
      })
    );
    dispatch(getRelatedCourseQuestBySubQuest({ subQuestId }));
    dispatch(subquestActions.toggleRemoveModal(true));
  };

  const generateQuiz = (targetSubQuest) => {
    dispatch(subquestActions.setQuestId(questId));
    dispatch(
      subquestActions.setQuest(
        quest.courseQuests?.find((questItem) => questItem.quest.id === questId)
          ?.quest
      )
    );
    dispatch(companionActions.setMode(CompanionMode.QUIZ_GENERATOR));
    dispatch(companionActions.setContext(targetSubQuest));
    dispatch(companionActions.setConversations([]));
    dispatch(companionActions.setModal(true));
    dispatch(companionActions.setInitialPrompt(true));
  };

  const detachSubQuest = (subQuestId) => {
    dispatch(
      subquestActions.setDetachSubQuest({
        courseId,
        questId,
        id: subQuestId,
        modal: true,
      })
    );
  };

  const iconColor = useColorModeValue('teal', 'white');
  const grIconColor = useColorModeValue('#000000', '#FFFFFF');

  return (
    <div ref={setNodeRef} style={style}>
      <Box mb={6} borderWidth="1px" borderRadius="md">
        <Box w="100%" p={4} bg={useColorModeValue('#f7f7f7', 'gray.800')}>
          <Flex align="center">
            {!isPreviewMode && (
              <IconButton
                {...attributes}
                {...listeners}
                variant="plain"
                size="lg"
                color="teal"
                aria-label="Re-order sub-quest"
                icon={<DragHandleIcon />}
              />
            )}

            <SubQuestIcon
              typeId={subQuest.type.id}
              boxSize={10}
              mx={4}
              color={iconColor}
            />

            <Box flex={1}>
              <Text fontWeight={500} fontSize="md" mb={1}>
                <IdTag id={subQuest.id} />
                &nbsp;
                {subQuest.name}
              </Text>
              <Text fontSize="sm">{removeHtmlTags(subQuest.objective)}</Text>

              <EmailGroup
                mt={2}
                name="Contributors:"
                emails={subQuest.ownerGroups}
              />
            </Box>

            <PublishButton
              entityType="SubQuest"
              entityId={subQuest.id}
              isPublished={subQuest.isPublished}
              mr={2}
            />

            {subQuest.subQuestNarrative && !isPreviewMode && (
              <Tooltip label="Generate Quiz" fontSize="md">
                <IconButton
                  onClick={() => {
                    generateQuiz(
                      removeHtmlTags(subQuest.subQuestNarrative.content)
                    );
                  }}
                  icon={<RiAiGenerate />}
                  size="sm"
                  variant="ghost"
                />
              </Tooltip>
            )}

            {subQuest.subQuestVideo?.transcript && !isPreviewMode && (
              <Tooltip label="Generate Quiz" fontSize="md">
                <IconButton
                  onClick={() => {
                    generateQuiz(subQuest.subQuestVideo.transcript);
                  }}
                  icon={<RiAiGenerate />}
                  size="sm"
                  variant="ghost"
                />
              </Tooltip>
            )}

            {subQuest.subQuestInteractiveVideo?.transcript &&
              !isPreviewMode && (
                <Tooltip label="Generate Quiz" fontSize="md">
                  <IconButton
                    onClick={() => {
                      generateQuiz(
                        subQuest.subQuestInteractiveVideo.transcript
                      );
                    }}
                    icon={<RiAiGenerate />}
                    size="sm"
                    variant="ghost"
                  />
                </Tooltip>
              )}

            {isEditable && !isPreviewMode && (
              <Tooltip label="Sub-Quest Report" fontSize="md">
                <LinkBox
                  as={ReactRouterLink}
                  to={`/admin/reports/sub-quests/${subQuest.id}`}
                >
                  <IconButton icon={<BiPieChart />} size="sm" variant="ghost" />
                </LinkBox>
              </Tooltip>
            )}

            {isEditable && !isPreviewMode && (
              <Menu>
                <MenuButton
                  isLoading={status === ReducerStatus.LOADING}
                  as={IconButton}
                  aria-label="Options"
                  icon={<HiDotsVertical />}
                  variant="text"
                />
                <MenuList>
                  <MenuItem
                    icon={<EditIcon />}
                    onClick={() => {
                      editSubQuest(subQuest.id);
                    }}
                  >
                    Edit
                  </MenuItem>
                  {subQuest.subQuestVideo?.transcript && (
                    <MenuItem
                      icon={<RiAiGenerate />}
                      onClick={() => {
                        generateQuiz(subQuest.subQuestVideo.transcript);
                      }}
                    >
                      Generate Quiz
                    </MenuItem>
                  )}
                  {subQuest.subQuestInteractiveVideo?.transcript && (
                    <MenuItem
                      icon={<RiAiGenerate />}
                      onClick={() => {
                        generateQuiz(
                          subQuest.subQuestInteractiveVideo.transcript
                        );
                      }}
                    >
                      Generate Quiz
                    </MenuItem>
                  )}
                  <MenuItem
                    icon={
                      <Box as="span" sx={{ '& *': { stroke: grIconColor } }}>
                        <GrDetach />
                      </Box>
                    }
                    onClick={() => {
                      detachSubQuest(subQuest.id);
                    }}
                  >
                    Detach
                  </MenuItem>
                  <MenuItem
                    icon={<DeleteIcon />}
                    onClick={() => {
                      removeSubQuest(subQuest.id);
                    }}
                  >
                    Remove
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
          </Flex>
        </Box>
      </Box>
    </div>
  );
}

SubQuestAdminItem.defaultProps = {
  id: 1,
  isPreviewMode: false,
};

SubQuestAdminItem.propTypes = {
  id: PropTypes.number,
  courseId: PropTypes.number.isRequired,
  questId: PropTypes.number.isRequired,
  questName: PropTypes.string.isRequired,
  isEditable: PropTypes.bool.isRequired,
  subQuest: PropTypes.object.isRequired,
  isPreviewMode: PropTypes.bool,
};

export default SubQuestAdminItem;
